import styled from 'styled-components';
import {
  compose,
  color,
  ColorProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
  shadow,
  ShadowProps,
  layout,
  LayoutProps,
  borders,
  BordersProps
} from 'styled-system';
import { colorLuminance } from '../../../utils/colors';

export type StyledIconProps = ColorProps &
  SpaceProps &
  TypographyProps &
  ShadowProps &
  LayoutProps &
  BordersProps;
export type StyledCheckboxProps = ColorProps &
  SpaceProps &
  TypographyProps &
  ShadowProps &
  LayoutProps &
  BordersProps &
  React.InputHTMLAttributes<HTMLInputElement>;
interface IIconProps extends StyledIconProps {
  checked?: boolean;
}

export interface IStyledCheckboxProps extends StyledCheckboxProps {
  /**
   * boolean value to make box ticked or not ticked by default
   *
   * @default false
   */
  checked?: boolean;
  /**
   * boolean value for when checkbox is disabled
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * function Event listener that handles change
   */
  onChange?: React.FormEventHandler<HTMLElement>;
}

export const StyledCheckboxIcon = styled.svg<IIconProps>`
  ${({ theme, checked }) => `
    fill: ${theme.colors.grayScale[0]};
    stroke: none;
    stroke-width: 1;
    visibility: ${checked ? 'visible' : 'hidden'};
  `}

  ${compose(color, typography, space, shadow, layout, borders)}
`;

export const StyledCheckbox = styled.div.attrs(() => ({
  'tab-index': 0
}))<IStyledCheckboxProps>`
  ${({ theme, disabled, checked }) => `
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 1px solid;
    transition: all 0.1s ease-in;
    cursor: ${disabled ? 'not-allowed' : 'pointer'};

    ${
      !checked && !disabled
        ? `border-color: ${theme.colors.form.input.border.enabled}; background-color: ${theme.colors.grayScale[0]};`
        : ''
    }
    ${
      checked && !disabled
        ? `border-color: transparent; background-color: ${colorLuminance(
            theme.colors.form.input.border.focus,
            0.2
          )};`
        : ''
    }
    ${
      !checked && disabled
        ? `border-color: transparent; background-color: ${theme.colors.grayScale[0]};`
        : ''
    }
    ${
      checked && disabled
        ? `border-color: ${theme.colors.form.input.border.disabled}; background-color: ${theme.colors.form.input.border.disabled};`
        : ''
    }

    &:hover {
      ${
        checked
          ? `background-color: ${colorLuminance(
              colorLuminance(theme.colors.form.input.border.focus, 0.2),
              0.2
            )};`
          : `border-color: ${
              disabled
                ? theme.colors.form.input.border.disabled
                : colorLuminance(theme.colors.form.input.border.focus, 0.2)
            };`
      }
    }

    input:focus + div {
      border-color: ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
      box-shadow: 0px 0px 0px 1px ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
    }
  `}

  ${compose(color, typography, space, shadow, layout, borders)}
`;

export const StyledHiddenCheckbox = styled.input.attrs(() => ({
  type: 'checkbox'
}))<IStyledCheckboxProps>`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
  box-shadow: none;
  transition: all 0.1s ease-in;

  ${({ theme, disabled }) => `
    margin: ${theme.space[0]}px;
    padding: ${theme.space[0]}px;

    &:focus + ${StyledCheckbox} {
      border-color: ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
      box-shadow: 0px 0px 0px 1px ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
    }

    &:active + ${StyledCheckbox} {
      border-color: ${colorLuminance(
        theme.colors.form.input.border.focus,
        0.2
      )};
    }

    &:hover:not(:disabled) + ${StyledCheckbox} {
      cursor: pointer;
      border-color: ${theme.colors.form.input.border.focus};
    }

    &:disabled + ${StyledCheckbox} {
      cursor: not-allowed;
      border-color: ${theme.colors.form.input.border.disabled};
    }

    & + label {
      cursor: ${disabled ? 'not-allowed' : 'pointer'};
    }
  `}

  ${compose(color, typography, space, shadow, layout, borders)}
`;
